import React, { useState, useMemo } from 'react';
import { useData } from '../DataContext';
import { api } from '../services/api';
import { format } from 'date-fns';
import { X } from 'lucide-react';

const Haircuts = () => {
  const { haircuts, clients, loading, refreshHaircuts, refreshClients } = useData();
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showClientModal, setShowClientModal] = useState(false);
  const [viewingHaircut, setViewingHaircut] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    isWalkIn: true,
    client: '',
    type: 'Простая',
    amount: ''
  });
  const [newClientData, setNewClientData] = useState({
    name: '',
    phone: '',
    isWalkIn: false
  });

  const regularClients = useMemo(() =>
    clients.filter(c => !c.isWalkIn),
    [clients]
  );

  const formatAmountInput = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (!numbers) return '';
    return new Intl.NumberFormat('ru-RU').format(numbers);
  };

  const parseFormattedAmount = (value) => {
    return value.replace(/\s/g, '');
  };

  const handleAmountChange = (e) => {
    const formatted = formatAmountInput(e.target.value);
    setFormData({ ...formData, amount: formatted });
  };

  const handleView = (haircut) => {
    setViewingHaircut(haircut);
    setIsEditing(false);
    setFormData({
      isWalkIn: haircut.client?.isWalkIn || false,
      client: haircut.client?._id || '',
      type: haircut.type,
      amount: formatAmountInput(haircut.amount.toString())
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const submitData = {
        ...formData,
        amount: parseFormattedAmount(formData.amount) // number
      };

      if (viewingHaircut) {
        // First try known api methods, otherwise use fetch fallback
        try {
          if (typeof api.updateHaircut === 'function') {
            await api.updateHaircut(viewingHaircut._id, submitData);
          } else if (typeof api.editHaircut === 'function') {
            await api.editHaircut(viewingHaircut._id, submitData);
          } else if (typeof api.update === 'function') {
            await api.update('haircuts', viewingHaircut._id, submitData);
          } else {
            throw new Error('no_api_method');
          }
        } catch (innerErr) {
          // Fallback: use fetch directly and provide detailed error if not OK
          const token = localStorage.getItem('token');
          const response = await fetch(`/api/haircuts/${viewingHaircut._id}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { 'Authorization': `Bearer ${token}` } : {})
            },
            body: JSON.stringify(submitData)
          });

          if (!response.ok) {
            const text = await response.text().catch(() => null);
            const json = await response.json().catch(() => null);
            console.error('PUT /api/haircuts/:id failed', { status: response.status, text, json, innerErr });
            throw new Error(`Сервер вернул ${response.status}: ${text || JSON.stringify(json)}`);
          }
        }
      } else {
        // create new
        if (typeof api.createHaircut === 'function') {
          await api.createHaircut(submitData);
        } else {
          const token = localStorage.getItem('token');
          const response = await fetch('/api/haircuts', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { 'Authorization': `Bearer ${token}` } : {})
            },
            body: JSON.stringify(submitData)
          });
          if (!response.ok) {
            const text = await response.text().catch(() => null);
            const json = await response.json().catch(() => null);
            console.error('POST /api/haircuts failed', { status: response.status, text, json });
            throw new Error(`Сервер вернул ${response.status}`);
          }
        }
      }

      // Успешно
      setShowModal(false);
      setViewingHaircut(null);
      setIsEditing(false);
      setFormData({
        isWalkIn: true,
        client: '',
        type: 'Простая',
        amount: ''
      });
      await refreshHaircuts();
    } catch (error) {
      console.error('Ошибка сохранения стрижки (в handleSubmit):', error);
      alert('Ошибка при сохранении стрижки: ' + (error.message || 'Unknown error'));
    } finally {
      setSubmitting(false);
    }
  };


  const handleCreateClient = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const response = await api.createClient(newClientData);
      await refreshClients();
      setShowClientModal(false);
      setNewClientData({
        name: '',
        phone: '',
        isWalkIn: false
      });
      setFormData({ ...formData, client: response.data._id });
      alert('Клиент успешно создан!');
    } catch (error) {
      console.error('Ошибка создания клиента:', error);
      alert('Ошибка при создании клиента');
    } finally {
      setSubmitting(false);
    }
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('uz-UZ').format(num);
  };

  const filteredHaircuts = useMemo(() =>
    haircuts.filter(h =>
      h.client?.name.toLowerCase().includes(search.toLowerCase()) ||
      h.type.toLowerCase().includes(search.toLowerCase())
    ),
    [haircuts, search]
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-400">Загрузка данных...</div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
        <h1 className="text-3xl font-bold text-white">Стрижки</h1>
        <button
          onClick={() => {
            setViewingHaircut(null);
            setIsEditing(false);
            setFormData({
              isWalkIn: true,
              client: '',
              type: 'Простая',
              amount: ''
            });
            setShowModal(true);
          }}
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-blue-600 text-white hover:bg-blue-800 font-semibold transition-all shadow-md"  
        >
          <span>Создать стрижку</span>
        </button>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-xl p-4">
          <input
            type="text"
            placeholder="Поиск по клиенту или типу стрижки..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-transparent text-white placeholder-gray-400 focus:outline-none"
          />
        </div>
      </div>

      {/* Desktop Table */}
      <div className="hidden lg:block bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-xl overflow-hidden">
        <table className="min-w-full divide-y divide-gray-700/50">
          <thead className="bg-gray-700/30">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                Дата
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                Клиент
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                Тип
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                Сумма
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                Мастер
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700/50">
            {filteredHaircuts.map((haircut) => (
              <tr
                key={haircut._id}
                className="hover:bg-gray-700/20 transition-colors cursor-pointer"
                onClick={() => handleView(haircut)}
              >
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                  {format(new Date(haircut.date), 'dd.MM.yyyy HH:mm')}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-white">
                  {haircut.client?.name}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                  {haircut.type}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-emerald-400">
                  {formatNumber(haircut.amount)} сўм
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                  {haircut.master?.username}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="lg:hidden space-y-4">
        {filteredHaircuts.map((haircut) => (
          <div
            key={haircut._id}
            className="bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-xl p-4 hover:border-gray-600/50 transition-all cursor-pointer"
            onClick={() => handleView(haircut)}
          >
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="font-semibold text-lg text-white">{haircut.client?.name}</h3>
                <p className="text-sm text-gray-400">{format(new Date(haircut.date), 'dd.MM.yyyy HH:mm')}</p>
              </div>
              <span className="bg-gray-700/50 border border-gray-600/50 text-gray-300 px-3 py-1 rounded-full text-sm">
                {haircut.type}
              </span>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-gray-400 text-sm">Сумма:</span>
                <span className="font-bold text-emerald-400">{formatNumber(haircut.amount)} сўм</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-400 text-sm">Мастер:</span>
                <span className="text-gray-300">{haircut.master?.username}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {filteredHaircuts.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-400">Стрижки не найдены</p>
        </div>
      )}

      {/* Main Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-gray-800 border border-gray-700 rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-white">
                  {viewingHaircut ? 'Информация о стрижке' : 'Новая стрижка'}
                </h2>
                <button
                  onClick={() => {
                    setShowModal(false);
                    setViewingHaircut(null);
                    setIsEditing(false);
                  }}
                  className="text-gray-400 hover:text-gray-300"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {viewingHaircut && !isEditing ? (
                <div className="space-y-4">
                  <div className="bg-gray-700/30 rounded-lg p-4 space-y-3">
                    <div>
                      <span className="text-gray-400 text-sm">Дата:</span>
                      <p className="text-white font-medium">{format(new Date(viewingHaircut.date), 'dd.MM.yyyy HH:mm')}</p>
                    </div>
                    <div>
                      <span className="text-gray-400 text-sm">Клиент:</span>
                      <p className="text-white font-medium">{viewingHaircut.client?.name}</p>
                    </div>
                    {viewingHaircut.client?.phone && (
                      <div>
                        <span className="text-gray-400 text-sm">Телефон:</span>
                        <p className="text-white font-medium">{viewingHaircut.client?.phone}</p>
                      </div>
                    )}
                    <div>
                      <span className="text-gray-400 text-sm">Тип клиента:</span>
                      <p className="text-white font-medium">{viewingHaircut.client?.isWalkIn ? 'Накд клиент' : 'Постоянный клиент'}</p>
                    </div>
                    <div>
                      <span className="text-gray-400 text-sm">Тип стрижки:</span>
                      <p className="text-white font-medium">{viewingHaircut.type}</p>
                    </div>
                    <div>
                      <span className="text-gray-400 text-sm">Сумма:</span>
                      <p className="text-emerald-400 font-bold text-lg">{formatNumber(viewingHaircut.amount)} сўм</p>
                    </div>
                    <div>
                      <span className="text-gray-400 text-sm">Мастер:</span>
                      <p className="text-white font-medium">{viewingHaircut.master?.username}</p>
                    </div>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <button
                      onClick={() => setIsEditing(true)}
                      className="flex-1 btn-primary"
                    >
                      Изменить
                    </button>

                    <button
                      onClick={async () => {
                        if (!window.confirm('Вы уверены, что хотите удалить эту стрижку?')) return;

                        try {
                          const token = localStorage.getItem('token');

                          // Сначала пробуем через api, если есть метод
                          if (typeof api.deleteHaircut === 'function') {
                            await api.deleteHaircut(viewingHaircut._id);
                          } else {
                            // Fallback — прямой запрос
                            const response = await fetch(`/api/haircuts/${viewingHaircut._id}`, {
                              method: 'DELETE',
                              headers: {
                                'Content-Type': 'application/json',
                                ...(token ? { 'Authorization': `Bearer ${token}` } : {})
                              }
                            });

                            if (!response.ok) {
                              const text = await response.text().catch(() => null);
                              throw new Error(`Ошибка удаления: ${text || response.status}`);
                            }
                          }

                          alert('Стрижка успешно удалена!');
                          setShowModal(false);
                          setViewingHaircut(null);
                          await refreshHaircuts();
                        } catch (error) {
                          console.error('Ошибка удаления стрижки:', error);
                          alert('Ошибка при удалении стрижки: ' + (error.message || 'Unknown error'));
                        }
                      }}
                      className="flex-1 btn-secondary text-red-400 hover:text-red-300"
                    >
                      Удалить
                    </button>
                  </div>

                </div>
              ) : (
                <div className="space-y-5">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="walkIn"
                      checked={formData.isWalkIn}
                      onChange={(e) => setFormData({ ...formData, isWalkIn: e.target.checked })}
                      className="w-5 h-5 text-gray-600 bg-gray-700 border-gray-600 rounded focus:ring-gray-500"
                    />
                    <label htmlFor="walkIn" className="ml-3 text-gray-300 font-medium">
                      Накд клиент
                    </label>
                  </div>

                  {!formData.isWalkIn && (
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <label className="block text-sm font-medium text-gray-300">
                          Клиент
                        </label>
                        <button
                          type="button"
                          onClick={() => setShowClientModal(true)}
                          className="text-sm text-blue-400 hover:text-blue-300 transition-colors"
                        >
                          + Добавить клиента
                        </button>
                      </div>
                      <select
                        value={formData.client}
                        onChange={(e) => setFormData({ ...formData, client: e.target.value })}
                        required={!formData.isWalkIn}
                        className="input-field"
                      >
                        <option value="">Выберите клиента</option>
                        {regularClients.map(client => (
                          <option key={client._id} value={client._id}>{client.name}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Тип стрижки
                    </label>
                    <select
                      value={formData.type}
                      onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                      className="input-field"
                    >
                      <option value="Простая">Простая</option>
                      <option value="Модельная">Модельная</option>
                      <option value="Детская">Детская</option>
                      <option value="Борода">Борода</option>
                      <option value="Комплекс">Комплекс</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Сумма (сўм)
                    </label>
                    <input
                      type="text"
                      value={formData.amount}
                      onChange={handleAmountChange}
                      required
                      placeholder="0"
                      className="input-field"
                    />
                  </div>

                  <div className="flex gap-3 pt-4">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        if (viewingHaircut) {
                          setIsEditing(false);
                        } else {
                          setShowModal(false);
                        }
                      }}
                      className="flex-1 btn-secondary"
                      disabled={submitting}
                    >
                      Отмена
                    </button>
                    <button
                      type="button"
                      onClick={handleSubmit}
                      className="flex-1 btn-primary"
                      disabled={submitting}
                    >
                      {submitting ? 'Сохранение...' : (viewingHaircut ? 'Сохранить' : 'Создать')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Client Modal */}
      {showClientModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
          <div className="bg-gray-800 border border-gray-700 rounded-2xl shadow-2xl max-w-md w-full">
            <div className="p-6">
              <h2 className="text-2xl font-bold text-white mb-6">Новый клиент</h2>

              <div className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Имя клиента *
                  </label>
                  <input
                    type="text"
                    value={newClientData.name}
                    onChange={(e) => setNewClientData({ ...newClientData, name: e.target.value })}
                    required
                    placeholder="Введите имя"
                    className="input-field"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Телефон
                  </label>
                  <input
                    type="tel"
                    value={newClientData.phone}
                    onChange={(e) => setNewClientData({ ...newClientData, phone: e.target.value })}
                    placeholder="+998 90 123 45 67"
                    className="input-field"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowClientModal(false);
                      setNewClientData({ name: '', phone: '', isWalkIn: false });
                    }}
                    className="flex-1 btn-secondary"
                    disabled={submitting}
                  >
                    Отмена
                  </button>
                  <button
                    type="button"
                    onClick={handleCreateClient}
                    className="flex-1 btn-primary"
                    disabled={submitting}
                  >
                    {submitting ? 'Создание...' : 'Создать клиента'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Haircuts;